/**
 * Manual start time (DNKADM-0390 U2) — what a front-desk user gets when available times
 * could not be loaded: a 12-hour hour / minute / AM-PM selector on the already-chosen date,
 * instead of a free-text "ISO-8601 with offset" box. The ISO start it raises carries the
 * location's own offset for that wall-clock time (built in code from the IANA zone, DST-safe);
 * the server still validates the booking exactly as before.
 *
 * `date` is normally the picker's chosen date. A caller with no picker (a degraded booking
 * with no service/variation/location) passes `onDateChange` and the date becomes editable.
 */
import { useId, useState } from 'react';
import { FormControl, FormLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { isoWallClock, wallClockToIso } from '@/utils/clockTime';

export interface ManualStartTimeProps {
  /** The `YYYY-MM-DD` the time is on; null when not yet chosen. */
  date: string | null;
  /** When given, the date is editable here (no picker owns it). */
  onDateChange?: (date: string) => void;
  /** The location's IANA zone; null → the device's offset. */
  timeZone: string | null;
  /** The current manual ISO start (seeds the controls), or null. */
  value: string | null;
  /** The ISO-with-offset start for the chosen wall clock, or null while incomplete. */
  onChange: (iso: string | null) => void;
  disabled?: boolean;
  label?: string;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '15', '30', '45'];

function seed(value: string | null): { hour: string; minute: string; period: 'AM' | 'PM' } {
  const clock = isoWallClock(value);
  if (!clock) return { hour: '', minute: '00', period: 'AM' };
  const h12 = clock.hour % 12 === 0 ? 12 : clock.hour % 12;
  const minute = String(Math.floor(clock.minute / 15) * 15).padStart(2, '0');
  return { hour: String(h12), minute, period: clock.hour >= 12 ? 'PM' : 'AM' };
}

export function ManualStartTime({
  date,
  onDateChange,
  timeZone,
  value,
  onChange,
  disabled = false,
  label = 'Pick a time manually',
}: ManualStartTimeProps) {
  const ids = useId();
  const [{ hour, minute, period }, setParts] = useState(() => seed(value));

  function raise(next: { hour: string; minute: string; period: 'AM' | 'PM' }, nextDate: string | null) {
    setParts(next);
    if (next.hour === '' || !nextDate) {
      onChange(null);
      return;
    }
    const h12 = Number(next.hour);
    const hour24 = next.period === 'AM' ? h12 % 12 : (h12 % 12) + 12;
    onChange(wallClockToIso(nextDate, hour24, Number(next.minute), timeZone));
  }

  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ mt: 1 }}>
      <FormLabel component="legend" id={`${ids}-legend`} sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
        {label}
      </FormLabel>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {onDateChange && (
          <TextField
            type="date"
            size="small"
            label="Date"
            value={date ?? ''}
            onChange={(e) => {
              onDateChange(e.target.value);
              raise({ hour, minute, period }, e.target.value || null);
            }}
            disabled={disabled}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'aria-label': 'Manual date' } }}
            sx={{ width: 170 }}
          />
        )}
        <Select
          size="small"
          value={hour}
          displayEmpty
          onChange={(e) => raise({ hour: String(e.target.value), minute, period }, date)}
          disabled={disabled}
          slotProps={{ input: { 'aria-label': 'Manual hour' } }}
          sx={{ minWidth: 88 }}
          renderValue={(v) => (v === '' ? <Typography sx={{ color: 'text.secondary' }}>Hour</Typography> : v)}
        >
          {HOURS.map((h) => (
            <MenuItem key={h} value={String(h)}>
              {h}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={minute}
          onChange={(e) => raise({ hour, minute: String(e.target.value), period }, date)}
          disabled={disabled}
          slotProps={{ input: { 'aria-label': 'Manual minute' } }}
          sx={{ minWidth: 80 }}
        >
          {MINUTES.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={period}
          onChange={(e) => raise({ hour, minute, period: e.target.value as 'AM' | 'PM' }, date)}
          disabled={disabled}
          slotProps={{ input: { 'aria-label': 'Manual AM/PM' } }}
          sx={{ minWidth: 80 }}
        >
          <MenuItem value="AM">AM</MenuItem>
          <MenuItem value="PM">PM</MenuItem>
        </Select>
      </Stack>
    </FormControl>
  );
}
